/**
 * What the in-thread question card tells a decider above the answer
 * (agion/Agion#3446, challenge 2026-10-26-escalation-question-chat-card
 * Mid-Eval 1, and the owner's demo feedback of 2026-10-26): which specialist
 * asks, why a person is asked, and whether the evidence it saw holds
 * anything. All of it comes from what governance and the run serve; the
 * reasons are the runner's content-free codes in metadata.review_reasons,
 * put into words here. The card stays blind: nothing here reads a model
 * answer, a probability or a confidence.
 */

import type { Escalation } from '../types/escalations'
import type { EvidenceView } from './escalation-form'

type Translate = (key: string, fallback: string) => string

function nonEmpty(value: unknown): value is string {
  return typeof value === 'string' && value.trim() !== ''
}

/** The specialist that asks: the question's asker label, else the step's label; undefined when neither is served. */
export function questionAskerLabel(escalation: Escalation): string | undefined {
  const agentLabel = escalation.question?.asker?.agent_label
  if (nonEmpty(agentLabel)) return agentLabel
  const nodeLabel = escalation.metadata?.node_label
  return nonEmpty(nodeLabel) ? nodeLabel : undefined
}

/** One translated text: its key in the chat bundle and its English fallback. */
interface TextEntry {
  key: string
  fallback: string
}

/**
 * The runner's review_reasons codes, by rung of the ladder (owner ruling of
 * 2026-10-26, Mikko Alasaarela). Each rung gives at most one sentence; the
 * first group of a rung that the codes name is the one said.
 */
const DECISION_RUNG: ReadonlyArray<{ codes: readonly string[]; text: TextEntry }> = [
  {
    codes: ['decision_abstained', 'below_threshold', 'margin_too_small', 'score_missing'],
    text: { key: 'escalations.reviewReason.decisionCouldNotChoose', fallback: "The decision model couldn't choose." },
  },
  {
    codes: ['route_unavailable', 'timeout', 'rate_limited', 'provider_error', 'invalid_answer', 'state_too_large'],
    text: { key: 'escalations.reviewReason.decisionUnavailable', fallback: "The decision model wasn't available." },
  },
]

const CHAT_RUNG: ReadonlyArray<{ codes: readonly string[]; text: TextEntry }> = [
  {
    codes: ['agent_model_skipped'],
    text: {
      key: 'escalations.reviewReason.agentModelSkipped',
      fallback: "The mission asks a person directly when the decision model can't choose.",
    },
  },
  {
    codes: ['agent_route_unavailable'],
    text: { key: 'escalations.reviewReason.agentUnavailable', fallback: "The specialist's model wasn't available." },
  },
  {
    codes: ['agent_unsure'],
    text: { key: 'escalations.reviewReason.agentUnsure', fallback: "The specialist's model wasn't sure." },
  },
]

/**
 * Why a person is asked, in at most two plain sentences: the decision rung's,
 * then the chat rung's. Pure display text over the codes in
 * metadata.review_reasons; unknown codes are ignored, and undefined when no
 * known code is named (or the field is absent or not a list).
 */
export function reviewReasonText(escalation: Escalation, t: Translate): string | undefined {
  const codes = (escalation.metadata as Record<string, unknown> | undefined)?.['review_reasons']
  if (!Array.isArray(codes)) return undefined
  const named = (group: { codes: readonly string[] }) => group.codes.some((code) => codes.includes(code))
  const sentences = [DECISION_RUNG.find(named), CHAT_RUNG.find(named)]
    .filter((group): group is (typeof DECISION_RUNG)[number] => group !== undefined)
    .map((group) => t(group.text.key, group.text.fallback))
  return sentences.length > 0 ? sentences.join(' ') : undefined
}

/** Whether opened evidence holds nothing: every section, or the text, is empty. */
export function evidenceIsEmpty(view: EvidenceView): boolean {
  return view.kind === 'sections' ? view.sections.every((section) => section.text.trim() === '') : view.text.trim() === ''
}
